/**
 * Sincroniza o locale das bibliotecas de data com o idioma ativo.
 *
 * O projeto usa quatro libs de data/calendário, cada uma com uma API própria de
 * locale. Sem este módulo, trocar o idioma deixaria os calendários e as datas
 * formatadas em português.
 *
 * Ao adicionar um idioma novo, basta acrescentar uma entrada em LOCALES.
 */
import { enUS as dateFnsEnUS, es as dateFnsEs, ptBR as dateFnsPtBR } from 'date-fns/locale';
import moment from 'moment-timezone';
import 'moment/locale/pt-br';
import 'moment/locale/es';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import i18n, { idiomaAtual } from './index';
import { DEFAULT_LANGUAGE } from './config';

const LOCALES = {
    'pt-BR': {
        dateFns: dateFnsPtBR,
        // moment e FullCalendar usam códigos em minúsculo.
        moment: 'pt-br',
        fullCalendar: 'pt-br',
    },
    'en-US': {
        dateFns: dateFnsEnUS,
        // Inglês é o locale embutido das duas libs — não há arquivo en-us a importar.
        moment: 'en',
        fullCalendar: 'en',
    },
    'es': {
        dateFns: dateFnsEs,
        moment: 'es',
        fullCalendar: 'es',
    },
};

function localeDe(idioma) {
    return LOCALES[idioma] || LOCALES[DEFAULT_LANGUAGE];
}

/** Objeto de locale do date-fns, para passar em format()/parse(). */
export function localeDateFns() {
    return localeDe(idiomaAtual()).dateFns;
}

/** Código de locale do FullCalendar, para a prop `locale`. */
export function localeFullCalendar() {
    return localeDe(idiomaAtual()).fullCalendar;
}

/** Tag BCP-47 para libs que aceitam locale de Intl (react-calendar, toLocaleString). */
export function localeIntl() {
    return idiomaAtual();
}

/**
 * Aplica o locale nas libs que guardam estado global (moment e react-datepicker).
 * Chamado no bootstrap e a cada troca de idioma.
 */
export function sincronizarLocaleDeDatas() {
    const idioma = idiomaAtual();
    const locale = localeDe(idioma);

    moment.locale(locale.moment);

    // react-datepicker exige registrar o locale antes de defini-lo como padrão.
    registerLocale(idioma, locale.dateFns);
    setDefaultLocale(idioma);
}

// Registra todos os locales de uma vez, para que componentes que passam a prop
// `locale` explicitamente encontrem qualquer idioma já registrado.
Object.entries(LOCALES).forEach(([idioma, locale]) => {
    registerLocale(idioma, locale.dateFns);
});

sincronizarLocaleDeDatas();

i18n.on('languageChanged', sincronizarLocaleDeDatas);
